import { useEffect, useRef, useState } from 'react';
import { api } from '../api';

/**
 * Meta Embedded Signup. Loads the Facebook JS SDK, launches the signup flow
 * with our Tech Provider config_id, captures the agency's WABA + phone number
 * from the flow's sessionInfo message, then hands the authorization code back
 * to the backend which stores the token and wires up routing.
 */
declare global {
  interface Window {
    FB?: any;
    fbAsyncInit?: () => void;
  }
}

export function ConnectWhatsApp({ tenantId }: { tenantId?: string }) {
  const [status, setStatus] = useState<{ connected: boolean; whatsapp: Record<string, string> } | null>(
    null,
  );
  const [cfg, setCfg] = useState<{ appId: string; configId: string; graphVersion: string } | null>(
    null,
  );
  const [msg, setMsg] = useState<string>('');
  const session = useRef<{ wabaId?: string; phoneNumberId?: string }>({});

  useEffect(() => {
    api.metaStatus(tenantId).then(setStatus).catch(() => undefined);
    api.metaConfig().then(setCfg).catch(() => undefined);
  }, [tenantId]);

  // Load the Facebook SDK once we know the app id + graph version.
  useEffect(() => {
    if (!cfg?.appId) return;
    window.fbAsyncInit = () => {
      window.FB?.init({ appId: cfg.appId, autoLogAppEvents: true, xfbml: true, version: cfg.graphVersion });
    };
    if (!document.getElementById('facebook-jssdk')) {
      const s = document.createElement('script');
      s.id = 'facebook-jssdk';
      s.src = 'https://connect.facebook.net/en_US/sdk.js';
      s.async = true;
      s.defer = true;
      document.body.appendChild(s);
    }

    const onMessage = (event: MessageEvent) => {
      if (!event.origin.endsWith('facebook.com')) return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type === 'WA_EMBEDDED_SIGNUP' && data?.event === 'FINISH') {
          session.current = {
            wabaId: data.data?.waba_id,
            phoneNumberId: data.data?.phone_number_id,
          };
        }
      } catch {
        /* non-JSON message from the SDK; ignore */
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [cfg]);

  function launch() {
    if (!window.FB || !cfg) {
      setMsg('Facebook SDK not ready yet, try again in a moment.');
      return;
    }
    setMsg('פותח את תהליך החיבור…');
    window.FB.login(
      async (response: any) => {
        const code = response?.authResponse?.code;
        if (!code) {
          setMsg('החיבור בוטל.');
          return;
        }
        const { wabaId, phoneNumberId } = session.current;
        if (!wabaId || !phoneNumberId) {
          setMsg('לא התקבלו פרטי המספר מ-Meta. נסה שוב.');
          return;
        }
        try {
          await api.metaConnect({ code, wabaId, phoneNumberId }, tenantId);
          setMsg('המספר חובר בהצלחה! ✓');
          api.metaStatus(tenantId).then(setStatus).catch(() => undefined);
        } catch (e) {
          setMsg('החיבור נכשל: ' + String(e));
        }
      },
      {
        config_id: cfg.configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: { setup: {}, featureType: 'whatsapp_business_app_onboarding' },
      },
    );
  }

  return (
    <div className="editor">
      <h2>חיבור מספר וואטסאפ עסקי</h2>

      <div className="card">
        {status?.connected ? (
          <>
            <p>מחובר ✓</p>
            <div className="meta">Phone number id: {status.whatsapp.phoneNumberId}</div>
            <div className="meta">WABA: {status.whatsapp.wabaId}</div>
          </>
        ) : (
          <p>עדיין לא חובר מספר. לחצ/י על הכפתור כדי לחבר את מספר הוואטסאפ של הסוכנות דרך Meta.</p>
        )}
      </div>

      <button className="btn" onClick={launch} disabled={!cfg?.configId}>
        חבר מספר וואטסאפ (Embedded Signup)
      </button>
      {!cfg?.configId && <p className="empty">Meta config_id לא הוגדר בשרת.</p>}
      {msg && <p className="badge" style={{ marginTop: 12 }}>{msg}</p>}
    </div>
  );
}
